import { useCallback, useEffect, useState } from "react";
import { container } from "tsyringe";
import MediaResponseResource from "common/resources/Project/Config/Media/MediaResponseResource";
import LazyImage from "components/elements/LazyImage";
import Typography from "components/elements/Typography";
import { useProjectId } from "hooks/useProjectId";
import MediaService from "services/projects/MediaService";
import { translateISO369 } from "utils/I18nText";
import classes from "./classes.module.scss";

const mediaService = container.resolve(MediaService);

type IProps = {
	media: MediaResponseResource;
	onOpen: (media: MediaResponseResource) => void;
};

export default function VideoMediaCard(props: IProps) {
	const { previewImage } = usePreviewImage(props.media);
	const { iframe } = useIframe(props.media);

	const handleClick = useCallback(() => {
		iframe && props.onOpen(props.media);
	}, [iframe, props]);

	return (
		<div className={classes["root"]}>
			{iframe && !previewImage ? (
				<div className={classes["iframe-container"]} onClick={handleClick} dangerouslySetInnerHTML={{ __html: iframe }} />
			) : (
				<div className={classes["iframe-container"]} onClick={handleClick}>
					{<LazyImage src={previewImage} alt={"URL preview"} className={classes["media-preview"]} />}
				</div>
			)}

			{props.media.caption && (
				<Typography type="p" weight="bold" className={classes["title"]}>
					{translateISO369(props.media.caption)}
				</Typography>
			)}
		</div>
	);
}

const usePreviewImage = (media: Pick<MediaResponseResource, "id" | "previewImage">) => {
	const { projectId } = useProjectId();
	const [previewImage, setPreviewImage] = useState<string | null>(null);

	useEffect(() => {
		if (!media.previewImage) return;

		mediaService.downloadPreviewImage(projectId, media.id).then((res) => {
			setPreviewImage(URL.createObjectURL(res));
		});
	}, [media.id, media.previewImage, projectId]);

	return { previewImage };
};

const useIframe = (media: Pick<MediaResponseResource, "id">) => {
	const { projectId } = useProjectId();
	const [iframe, setIframe] = useState<string | null>(null);

	useEffect(() => {
		mediaService.getIframe(projectId, media.id).then((res) => setIframe(res.iframe));
	}, [media.id, projectId]);

	return { iframe };
};
